// /permits - every permit the server lets this persona see, with filters kept in the
// URL (so "Your permits" can link to ?mine=1).
import { useId } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ErrorBanner } from '@/components/evidence'
import { Banner, Button, Card, EmptyState, Field, PageHeader, Select, Spinner, TextInput } from '@/components/ui'
import { PERMIT_STATES } from '@/domain/catalogue'
import { fmtDateTime } from '@/lib/format'
import { useMe, usePermits } from '@/lib/queries'
import { LinkButton } from './LinkButton'
import { PermitCard } from './PermitCard'
import { canStartPermit, filterPermits, type PermitFilter, plural } from './permitList'

const STATE_OPTIONS = Object.entries(PERMIT_STATES).map(([value, s]) => ({ value, label: s.label }))

export function PermitListPage() {
  const [params, setParams] = useSearchParams()
  const permits = usePermits()
  const me = useMe()
  const searchId = useId()
  const stateId = useId()
  const filter: PermitFilter = { state: params.get('state') ?? '', q: params.get('q') ?? '', mine: params.get('mine') === '1' }
  const filtered = Boolean(filter.state || filter.q || filter.mine)

  const update = (patch: Partial<PermitFilter>) => {
    const next = { ...filter, ...patch }
    const sp = new URLSearchParams()
    if (next.q) sp.set('q', next.q)
    if (next.state) sp.set('state', next.state)
    if (next.mine) sp.set('mine', '1')
    setParams(sp, { replace: true })
  }

  const list = permits.data ?? []
  const shown = filterPermits(list, filter)

  return (
    <div className="space-y-4">
      <PageHeader
        title="Permits"
        subtitle="Every permit you can see on your sites."
        actions={me.data && canStartPermit(me.data) ? <LinkButton to="/permits/new">New permit</LinkButton> : null}
      />
      <Card>
        <div className="grid gap-x-4 sm:grid-cols-[1fr_18rem]">
          <Field label="Search" htmlFor={searchId}>
            <TextInput
              id={searchId}
              type="search"
              value={filter.q}
              onChange={(e) => update({ q: e.target.value })}
              placeholder="Permit number, title or area"
              autoComplete="off"
            />
          </Field>
          <Field label="Status" htmlFor={stateId}>
            <Select id={stateId} value={filter.state} onChange={(e) => update({ state: e.target.value })} options={STATE_OPTIONS} placeholder="All statuses" />
          </Field>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <label className="tap flex cursor-pointer items-center gap-3 text-sm text-ink">
            <input type="checkbox" checked={filter.mine} onChange={(e) => update({ mine: e.target.checked })} />
            Only permits I am named on or need to act on
          </label>
          {filtered ? (
            <Button variant="ghost" onClick={() => setParams(new URLSearchParams(), { replace: true })}>
              Clear filters
            </Button>
          ) : null}
        </div>
      </Card>

      {permits.isLoading ? (
        <div className="flex justify-center p-6">
          <Spinner />
        </div>
      ) : !permits.data ? (
        <ErrorBanner error={permits.error ?? new Error('Permits are not available.')} title="Could not load permits" />
      ) : (
        <>
          {permits.isError ? (
            <Banner tone="warn" title="Showing the last list loaded">
              Connection problem. This list is from {fmtDateTime(new Date(permits.dataUpdatedAt).toISOString())} and may be out of
              date.
            </Banner>
          ) : null}
          <p className="text-sm text-ink-soft" aria-live="polite">
            {filtered ? `${shown.length} of ${plural(list.length, 'permit')} match` : plural(list.length, 'permit')}
          </p>
          {shown.length === 0 ? (
            <EmptyState title={list.length === 0 ? 'No permits yet' : 'No permits match these filters'}>
              {list.length === 0 ? 'Permits appear here once a Permit Applicant starts a draft.' : 'Change or clear the filters.'}
            </EmptyState>
          ) : (
            <ul className="space-y-2">
              {shown.map((p) => (
                <li key={p.id}>
                  <PermitCard p={p} />
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  )
}
